// 병동 간 재분배(옵션2) 순수 로직. 모달과 단위테스트가 공유 (React 비의존).

// 팀 버킷: team_id 가 있으면 기존 팀, null 이면 '가분할'(저장 시 team_label 로 생성).
export type RedistributeTeamBucket = {
  team_id: number | null;
  members: { nurse_id: string; name?: string }[];
};

export type RedistributeWard = {
  name?: string;
  size: number;
  nurse_ids: string[];
  teams: Record<string, RedistributeTeamBucket>;
};

export type ExcludedNurse = {
  nurse_id: string;
  name?: string;
  group_id?: string;
  group_name?: string;
};

export type RosterMember = {
  nurse_id: string;
  name: string;
  grade?: number | null;
  shift: string; // "전체" | "N" | "D E" ...
  is_night: boolean;
  preceptor_name?: string | null;
  is_preceptor?: boolean;
  group_id?: string;
};

export type RedistributePreview = {
  target_month: string;
  num_wards: number;
  num_pool: number;
  num_participants?: number;
  num_excluded_night: number;
  excluded_night?: ExcludedNurse[];
  num_excluded_overlap?: number;
  excluded_overlap?: ExcludedNurse[];
  num_fixed_stay?: number;
  fixed_stay?: ExcludedNurse[];
  capacity_mode: string;
  warnings: string[];
  wards: Record<string, RedistributeWard>;
  moves: {
    nurse_id: string;
    name?: string;
    from_name?: string;
    to_name?: string;
  }[];
  num_moved: number;
  pool_roster?: RosterMember[];
  pairs?: {
    preceptee_id: string;
    preceptee_name?: string;
    preceptor_id: string;
    preceptor_name?: string;
    status: string;
    moved?: boolean;
  }[];
};

/**
 * preview.wards(그룹→팀) 에 배치된 모든 간호사를 풀 로스터로 평탄화.
 * (참여 선택 체크박스의 후보 목록 — 이름/현재배치 병동 포함)
 */
export function extractPoolRoster(
  wards: Record<string, RedistributeWard>
): { nurse_id: string; name?: string; group_id: string }[] {
  const seen = new Set<string>();
  const out: { nurse_id: string; name?: string; group_id: string }[] = [];
  Object.entries(wards).forEach(([gid, w]) => {
    Object.values(w.teams).forEach((bucket) =>
      bucket.members.forEach((m) => {
        if (seen.has(m.nurse_id)) return;
        seen.add(m.nurse_id);
        out.push({ nurse_id: m.nurse_id, name: m.name, group_id: gid });
      })
    );
  });
  return out;
}

/**
 * preview.wards(그룹→팀버킷→간호사) → /teams/redistribute/apply 의 assignments.
 * 버킷 team_id 로 분기(라벨 문자열 추측 안 함):
 *   - team_id != null → 기존 팀: { team_id }
 *   - team_id == null & 라벨이 '전체' 아님 → 가분할: { team_label: label } (저장 시 팀 생성)
 *   - team_id == null & 라벨 '전체' → 팀 미적용(둘 다 생략)
 */
export function buildRedistributeAssignments(
  wards: Record<string, RedistributeWard>
): {
  nurse_id: string;
  to_group_id: string;
  team_id?: number | null;
  team_label?: string;
}[] {
  const out: {
    nurse_id: string;
    to_group_id: string;
    team_id?: number | null;
    team_label?: string;
  }[] = [];
  Object.entries(wards).forEach(([gid, w]) => {
    Object.entries(w.teams).forEach(([label, bucket]) => {
      const extra =
        bucket.team_id != null
          ? { team_id: bucket.team_id }
          : label.trim() !== "" && label !== "전체"
            ? { team_label: label }
            : {};
      bucket.members.forEach((m) =>
        out.push({ nurse_id: m.nurse_id, to_group_id: gid, ...extra })
      );
    });
  });
  return out;
}
